<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, computed, watch, getCurrentInstance, nextTick } from 'vue'
import _ from 'lodash'
import { useNotification } from "@kyvg/vue3-notification"
import MetricsService from '@/services/MetricsService'
import UserService from '@/services/UserService'
import SubscriptionService from '@/services/SubscriptionService'
import { useStore } from 'vuex'
import FormButton from '../../components/Atoms/FormButton.vue'
import TabGroup from '../../components/Atoms/TabGroup.vue'
import TabItem from '../../components/Atoms/TabItem.vue'
import Badge from '../../components/Badge.vue'
import Spinner from '../../components/ui/Spinner.vue'
import { date, bytes } from '../../filtersNew'
import SubscriptionCancelModal from '../../components/modals/SubscriptionCancelModal.vue'
import AlertModal from '../../components/modals/AlertModal.vue'
import IconBase from '../../components/icon/IconBase.vue'
import IconDownload from '../../components/icon/IconDownload.vue'
import IconInfo from '../../components/icon/IconInfo.vue'
import Tooltip from '../../components/Atoms/Tooltip.vue'
import ProjectUsagePanel from '@/components/project/ProjectUsagePanel.vue'
import { track, EVENTS } from '@/lib/tracking'

// Store, route, notification
const store = useStore()
const { notify } = useNotification()
const instance = getCurrentInstance();
const root = instance.proxy.$root;

// Computed properties
const subscriptions = computed(() => store.getters['User/subscriptions'])

const userEmail = computed(() => store.getters['User/userEmail'])

const userId = computed(() => store.getters['User/userId'])

const isTeamModerator = computed(() => store.getters['User/isTeamModerator'])

const invoiceItems = ref(null)

// True until the user (and thus subscriptions) have finished their initial load.
// Distinguishes "still loading" from "loaded but genuinely no subscriptions" so
// we don't flash the empty/"no plans" state on a hard refresh.
const userLoaded = computed(() => store.getters['User/userLoaded'])
const loadingStatus = computed(() => !userLoaded.value)

const subscriptionsFiltered = computed(() => {
  if (activeTab.value === 'active') {
    const subscriptionsSorted = _.orderBy(subscriptions.value, ['category', 'parentPackage'], ['asc', 'desc'])
    return subscriptionsSorted.filter(subscription => subscription.enabled)
  } else {
    const subscriptionsSorted = _.orderBy(subscriptions.value, ['category', 'parentPackage'], ['desc', 'asc'])
    return subscriptionsSorted.filter(subscription => !subscription.enabled)
  }
})

const activeCount = computed(() => (subscriptions.value || []).filter(s => s.enabled).length)
const expiredCount = computed(() => (subscriptions.value || []).filter(s => !s.enabled).length)

// Refs
const activeTab = ref('active')
const configurableSubscription = ref(null)

//Methods
const setActiveTab = (tabName) => activeTab.value = tabName

const formatedDate = (endDate) => date(endDate, 'DD MMM, YYYY')

const isBundled = (sub) => sub.parentPackage

const subscriptionCirle = (sub) => {
  if (sub.package.cycleAge === 30) return 'month'
  if (sub.package.cycleAge === 365) return 'year'
  return
}

const subCategory = (sub) => {
  if (sub.package.category === 'live') return 'Live Streaming'
  if (sub.package.category === 'restream') return 'Mustistream'
  if (sub.package.category === 'vod') return 'Video Hosting'
  if (sub.package.category === 'ipcam') return 'IP Camera Restreaming'
  if (sub.package.category === 'scheduled') return 'Scheduler'
  if (sub.package.category === 'ott') return 'OTT'
  return sub.package.category
}

const getSubscriptionDefProp = (sub, prop) => {
  return _.get(sub, 'definitionOverride.' + prop) || _.get(sub, 'package.definition.' + prop)
}

const loadInvoices = async () => {
  try {
    let rawInvoices = await SubscriptionService.getUserInvoicePayments()
    const paidInvoices = _.filter(rawInvoices, item => {
      return _.isEmpty(_.get(item, 'nested', 'invoice-payments-list'), false) === false
    })

    let payments = []
    _.each(paidInvoices, (invoice, idx) => {
      const invoiceTitle = _.get(invoice, ['nested', 'invoice-items', '0', 'item_title'], 'N/A')
      const paymentsList = _.get(invoice, ['nested', 'invoice-payments-list'], [])
      payments.push(_.map(paymentsList, elem => {
        let pdfLink = elem['pdf-invoice-link']
        if (elem.paysys_id === 'fastspring-contextual' && _.startsWith(elem.transaction_id, 'CASTR')) {
          pdfLink = `https://castrhq.onfastspring.com/account/order/${elem.transaction_id}/invoice/pdf`
        }

        return {
          date: elem.dattm,
          products: invoiceTitle,
          amount: `$${elem.amount}`,
          invoice: pdfLink
        }
      }))
    })

    payments = _.flatten(payments)
    invoiceItems.value = _.orderBy(payments, 'date', 'desc')
  } catch (e) { console.log('e', e) }
}

const BANDWIDTH_OVERRIDE_USER_ID = '65a508f3e2026e6faf40dfdc'

const uniqueSubsByPackage = (subs) => {
  const seen = new Set()
  return (subs || []).filter((sub) => {
    const pkgId = sub?.package?._id
    if (!pkgId || seen.has(pkgId)) return false
    seen.add(pkgId)
    return true
  })
}

const subscriptionCategory = (sub) => sub?.category || sub?.package?.category

const hasPositiveStorage = (sub) => Number(getSubscriptionDefProp(sub, 'storage')) > 0

const afterPaint = () => new Promise((resolve) => {
  if (typeof requestAnimationFrame !== 'function') {
    setTimeout(resolve, 0)
    return
  }
  requestAnimationFrame(() => requestAnimationFrame(resolve))
})

const applyUsage = (sub, payload) => store.dispatch('User/updateAddonSubUsage', { sub, ...payload })

/**
 * Which usage figures are still being counted, as `${packageId}:${metric}`.
 *
 * Bandwidth is read across the whole billing cycle, which takes seconds on an
 * account with a year's worth of it. Until it landed there was simply no
 * Bandwidth tile: the page looked finished while a figure was still missing
 * from it, and then one appeared without warning.
 */
const counting = ref(new Set())
const usageKey = (sub, metric) => `${sub?.package?._id}:${metric}`
const METERED = ['bandwidth', 'storage', 'phoneAlerts']

const whileCounting = async (sub, metric, task) => {
  const key = usageKey(sub, metric)
  counting.value.add(key)
  try {
    return await task()
  } finally {
    counting.value.delete(key)
  }
}

/** Metrics this plan meters that have been asked for but have not answered. */
const countingMetrics = (sub) => METERED.filter((metric) => (
  counting.value.has(usageKey(sub, metric)) && !sub?.usage?.[metric]
))

const metricLabel = (metric) => (metric === 'phoneAlerts' ? 'Phone Alerts' : metric)

const loadBandwidthAndAlerts = async () => {
  await Promise.all(uniqueSubsByPackage(subscriptions.value).map((sub) => {
    const tasks = []

    const phoneAlerts = getSubscriptionDefProp(sub, 'phoneAlerts')
    if (phoneAlerts) {
      tasks.push((async () => {
        try {
          const res = (sub.package.category === 'restream')
            ? await MetricsService.getSubscriptionNotificationCount('restream')
            : await MetricsService.getSubscriptionNotificationCount()

          sub.package.category === 'restream'
            ? await store.dispatch('User/updateBaseSubUsage', { sub, phoneAlerts: { value: res ? res.sms : 0, unit: 'SMS' } })
            : await applyUsage(sub, { phoneAlerts: { value: res ? res.sms : 0, unit: 'SMS' } })
        } catch (e) {
          console.error('e', e)
        }
      })())
    }

    const bwLimit = getSubscriptionDefProp(sub, 'bandwidth')
    if (bwLimit) {
      tasks.push(whileCounting(sub, 'bandwidth', async () => {
        try {
          // Team members carry their own `_id` on the owner's profile; the
          // subscription belongs to the owner, so meter against the owner.
          const billingUserId = UserService.getUserId(userId.value) || userId.value
          let res = await MetricsService.getSubscriptionBandwidth(
            billingUserId,
            sub.package._id
          )
          // `partial` means one of the reads behind the figure was abandoned —
          // in practice the Content Library one, which runs for seconds cold
          // and under a second once warm. The first call has done the warming,
          // so asking once more usually answers in full instead of reporting
          // an account several GB short of what it actually used.
          if (res?.partial) {
            res = await MetricsService.getSubscriptionBandwidth(billingUserId, sub.package._id)
          }
          let currentBytes = res ? res.bytes : 0
          currentBytes = currentBytes >= 100000000 ? currentBytes : 0

          if (billingUserId === BANDWIDTH_OVERRIDE_USER_ID) {
            currentBytes = currentBytes + 1024 * 1024 * 1024 * 1024 * 29
          }
          await applyUsage(sub, { bandwidth: bytes(currentBytes, true, 3, true) })
        } catch (e) {
          console.error('e', e)
        }
      }))
    }

    return Promise.all(tasks)
  }))
}

const loadStorageUsage = async (subs = subscriptions.value) => {
  const withStorage = uniqueSubsByPackage((subs || []).filter(hasPositiveStorage))
  if (!withStorage.length) return

  const scheduled = withStorage.filter((s) => subscriptionCategory(s) === 'scheduled')
  const vodPool = withStorage.filter((s) => subscriptionCategory(s) !== 'scheduled')
  const uid = UserService.getUserId(userId.value)
  const maxVod = Number(store.getters['User/maxVodStorage']) || 0
  const canonicalVod =
    vodPool.find((s) => Number(getSubscriptionDefProp(s, 'storage')) === maxVod) || vodPool[0]

  const fetchAndApply = async (canonical, targets) => {
    if (!canonical?.package?._id) return
    const keys = targets.map((sub) => usageKey(sub, 'storage'))
    keys.forEach((key) => counting.value.add(key))
    try {
      const res = await MetricsService.getSubscriptionStorage(uid, canonical.package._id)
      const formatted = bytes(res ? res.bytes : 0, true, 3, true)
      await Promise.all(targets.map((sub) => applyUsage(sub, { storage: formatted })))
    } catch (e) {
      console.error('e', e)
    } finally {
      keys.forEach((key) => counting.value.delete(key))
    }
  }

  // Live/VOD share one account pool; scheduled storage is a separate quota.
  // Run those calculations one after another so the page never kicks off two
  // storage POSTs at the same time during first paint.
  if (canonicalVod) await fetchAndApply(canonicalVod, vodPool)
  for (const sub of scheduled) {
    await fetchAndApply(sub, [sub])
  }
}

const loadSubscriptionsUsage = async ({ deferStorage = false } = {}) => {
  const bandwidthPromise = loadBandwidthAndAlerts()
  const storageSubs = deferStorage
    ? (subscriptions.value || []).filter((s) => s.enabled)
    : subscriptions.value
  if (deferStorage) {
    await nextTick()
    await afterPaint()
    loadStorageUsage(storageSubs)
    await bandwidthPromise
    return
  }
  await Promise.all([bandwidthPromise, loadStorageUsage(storageSubs)])
}

const getSubscriptionUsageData = () => {
  return Promise.all([loadInvoices(), loadSubscriptionsUsage({ deferStorage: true })])
}

const assignedFormatted = (value, property) => {
  if (property === 'phoneAlerts') return `${value} SMS`
  if (property === 'storage') {
    return value < 1
      ? `${value *= 1000} MB`
      : `${value} GB`
  }
  if (property === 'bandwidth') {
    return value < 1
      ? `${value *= 1000} MB`
      : (value > 1000)
        ? `${(value / 1000).toFixed(3)} TB`
        : `${value} GB`
  }
}

const normalizeToBase = (value, unit) => {
  const u = String(unit || '').toUpperCase()
  const v = Number(value) || 0
  switch (u) {
    case 'TB': return v * 1000
    case 'GB': return v
    case 'MB': return v / 1000
    case 'KB': return v / 1e6
    case 'B':
    case 'BYTES': return v / 1e9
    default: return v // SMS / unit-less counts
  }
}

// Percentage of assigned quota consumed (null when it can't be computed)
const usagePercent = (subscription, index, usageItem) => {
  const assigned = Number(getSubscriptionDefProp(subscription, index)) || 0
  if (!assigned) return null
  const used = normalizeToBase(usageItem.value, usageItem.unit)
  return Math.max(0, Math.min(100, (used / assigned) * 100))
}

// True when consumption has exceeded a finite assigned quota (over 100%).
// Returns false for unlimited / no-finite-allocation so we never show red there.
const isOverQuota = (subscription, index, usageItem) => {
  const assigned = Number(getSubscriptionDefProp(subscription, index)) || 0
  if (!assigned) return false
  const used = normalizeToBase(usageItem.value, usageItem.unit)
  return used > assigned
}

const usageBarWidth = (pct) => (pct > 0 ? Math.max(pct, 2) : 0) + '%'

const requestChangePaymentInfo = async () => {
  try {
    const result = await SubscriptionService.changePaymentInfo()
    if (result && result.ok === false && !result.link) {
      showModal('modal-change-payment-info-failed');
    }
    if (result && result.link) {
      window.location.href = result.link
    }
  } catch (e) {
    notify({
      group: 'error',
      text: 'Failed to request change payment info'
    });
  }
}

const requestUnsubscribe = (sub) => {
  configurableSubscription.value = sub
  showModal('modal-unsubscribe-package')
}

const unsubscribeSubCategory = async (cancelObj) => {
  const packCategory = configurableSubscription.value.category || 'restream'
  const cancelledPackage = configurableSubscription.value.package
  const cend = configurableSubscription.value.cend

  try {
    configurableSubscription.value.unsubscribeProcessing = true
    await SubscriptionService.unsubscribeSubscription(packCategory, cancelObj?.reason, cancelObj?.comment)
    configurableSubscription.value.unsubscribed = true
    configurableSubscription.value.unsubscribeProcessing = false
    await store.dispatch('User/loadUser')
    await loadSubscriptionsUsage()
    notify({ group: 'success', text: `Unsubscribed from the ${packCategory} package.` })
    track(EVENTS.SUBSCRIPTION_CANCELLED, {
      packCategory,
      planName: cancelledPackage?.name,
      baseCharge: cancelledPackage?.baseCharge,
      billingCycle: cancelledPackage?.cycleAge === 365 ? 'yearly' : 'monthly',
      cancelReason: cancelObj?.reason,
      subscriptionEndDate: cend,
    })
  } catch (e) {
    notify({ group: 'error', text: `failed to unsubscribe ${packCategory} package` })
  }
}

const getSubscriptionName = () => {
  if (!configurableSubscription.value) return
  let packname = configurableSubscription.value.package.name
  packname = _.startCase(packname)
  return packname
}

const dataLoaded = ref(false)
const usageLoading = ref(true)

watch(subscriptions, async (newValue) => {
  if (!newValue || newValue.length === 0) return;

  if (dataLoaded.value) return
  dataLoaded.value = true

  // Land on the tab that actually has content (e.g. when the only plan has expired)
  if (activeCount.value === 0 && expiredCount.value > 0) {
    activeTab.value = 'expired'
  }

  try {
    await getSubscriptionUsageData()
  } finally {
    usageLoading.value = false
  }
}, {
  immediate: true
})
</script>

<template>
  <div>
    <Spinner v-if="loadingStatus" text="Loading your subscriptions…" classes="text-dark-8 mh-60" spinner-color="var(--c-dark-8)"
      spinner-size="15px" />
    <div v-else-if="!isTeamModerator">
      <header class="w-full flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 !mt-2">
        <h2 class="text-xl font-medium">Subscriptions</h2>
        <span class="text-surface-7 sm:ml-auto text-sm min-w-0 break-all">Email: <span class="text-surface-9">{{ userEmail
            }}</span></span>
        <FormButton class="w-full sm:w-auto" type="secondary" @click="requestChangePaymentInfo()">
          Change Payment Details
        </FormButton>
      </header>
      <main class="!py-4">
        <TabGroup v-if="expiredCount > 0" type="segmented" custom-classes="mb-5">
          <TabItem :is-active="activeTab === 'active'" @click="setActiveTab('active')">
            Active
            <span class="text-tiny ml-1.5" :class="activeTab === 'active' ? 'text-surface-8' : 'text-surface-6'">{{ activeCount }}</span>
          </TabItem>
          <TabItem :is-active="activeTab === 'expired'" @click="setActiveTab('expired')">
            Expired
            <span class="text-tiny ml-1.5" :class="activeTab === 'expired' ? 'text-surface-8' : 'text-surface-6'">{{ expiredCount }}</span>
          </TabItem>
        </TabGroup>
        <section v-if="subscriptionsFiltered.length > 0">
          <ul class="flex flex-col gap-4">
            <li class="bg-surface-1 rounded-xl p-4 sm:p-5 min-w-0" v-for="subscription in (subscriptionsFiltered)"
              :key="subscription.package._id">
              <header class="flex flex-wrap items-start gap-x-4 gap-y-3">
                <div class="min-w-0">
                  <div class="flex items-center gap-2 flex-wrap">
                    <h3 class="text-lg font-semibold">{{ subscription.package.name }}</h3>
                    <span class="inline-flex items-center gap-1.5 text-xs font-medium"
                      :class="activeTab === 'active' ? 'text-success' : 'text-danger'">
                      <span class="size-1.5 rounded-full" :class="activeTab === 'active' ? 'bg-success' : 'bg-danger'"></span>
                      {{ activeTab === 'active' ? 'Active' : 'Expired' }}
                    </span>
                  </div>
                  <div class="flex items-center gap-2 mt-2 flex-wrap">
                    <Badge class="!inline-flex" :title="subCategory(subscription)" />
                    <span v-if="!isBundled(subscription)" class="text-sm text-surface-8 font-medium">${{
                      subscription.package.baseCharge }}/{{ subscriptionCirle(subscription) }}</span>
                  </div>
                </div>
                <div v-if="isBundled(subscription)" class="md:ml-auto">
                  <Badge class="!inline-flex" title="Bundled" />
                </div>
                <div v-else class="w-full md:w-auto md:ml-auto flex flex-col md:flex-row md:items-center gap-3">
                  <div class="text-sm text-surface-8 flex items-center gap-1.5">
                    <span>{{ activeTab === 'active' ? 'Valid until' : 'Expired' }}</span>
                    <span :class="activeTab === 'active' ? 'text-success' : 'text-danger'">{{
                      formatedDate(subscription.cend) }}</span>
                  </div>
                  <div class="grid grid-cols-1 sm:grid-cols-2 md:flex md:items-center gap-2">
                    <FormButton class="w-full md:w-auto justify-center" size="sm" :label="activeTab === 'active' ? 'Change' : 'Renew'"
                      to="../subscribe?category=live" />
                    <FormButton class="w-full md:w-auto justify-center" size="sm" v-if="activeTab === 'active' && !subscription.unsubscribed" type="secondary"
                      label="Cancel" @click="requestUnsubscribe(subscription)" />
                    <FormButton class="w-full md:w-auto justify-center" size="sm" v-if="activeTab === 'active' && subscription.unsubscribed" disabled
                      type="secondary" label="Unsubscribed" />
                  </div>
                </div>
              </header>
              <template v-if="subscription.package?.category !== 'ott'">
                <div class="mt-5">
                  <h4 class="text-tiny uppercase tracking-wide text-surface-7 font-medium mb-3">Usage quota</h4>
                  <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                    <div v-if="getSubscriptionDefProp(subscription, 'streams')" class="bg-surface-2 rounded-lg p-3">
                      <p class="text-tiny text-surface-7 mb-1">Streams</p>
                      <p class="text-base font-medium">{{ getSubscriptionDefProp(subscription, 'streams') }}
                        <span class="text-surface-7 text-sm font-normal">assigned</span>
                      </p>
                    </div>
                    <template v-if="subscription.usage">
                      <div v-for="(usageItem, index) in subscription.usage" :key="index" class="bg-surface-2 rounded-lg p-3">
                        <p class="text-tiny text-surface-7 mb-1.5 flex items-center gap-1 capitalize">
                          {{ index === 'phoneAlerts' ? 'Phone Alerts' : index }}
                          <Tooltip v-if="index === 'storage'" content="Storage usage now includes all uploaded videos, transcoded files, and ad assets.">
                            <icon-base class="shrink-0 text-surface-7" width="14" height="14" fill="none">
                              <IconInfo />
                            </icon-base>
                          </Tooltip>
                        </p>
                        <p class="text-base font-semibold uppercase leading-none"
                          :class="isOverQuota(subscription, index, usageItem) ? 'text-error-light' : ''">{{ usageItem.value }} {{ usageItem.unit }}</p>
                        <p class="text-tiny text-surface-6 mt-1 normal-case">of {{ assignedFormatted(getSubscriptionDefProp(subscription, index), index) }}</p>
                        <div v-if="usagePercent(subscription, index, usageItem) !== null"
                          class="mt-2 h-1.5 rounded-full bg-surface-1 overflow-hidden">
                          <div class="h-full rounded-full transition-all"
                            :class="isOverQuota(subscription, index, usageItem) ? 'bg-error-light' : usagePercent(subscription, index, usageItem) >= 100 ? 'bg-danger' : usagePercent(subscription, index, usageItem) >= 80 ? 'bg-yellow' : 'bg-main'"
                            :style="{ width: usageBarWidth(usagePercent(subscription, index, usageItem)) }"></div>
                        </div>
                      </div>
                    </template>
                    <!-- A figure still being counted keeps its own tile rather
                         than appearing out of nowhere when the read lands. The
                         bar is indeterminate on purpose: the read gives no
                         progress to report, only that it is still running. -->
                    <div
                      v-for="metric in countingMetrics(subscription)"
                      :key="`counting-${metric}`"
                      class="bg-surface-2 rounded-lg p-3"
                      :data-testid="`usage-counting-${metric}`"
                    >
                      <p class="text-tiny text-surface-7 mb-1.5 capitalize">{{ metricLabel(metric) }}</p>
                      <!-- The spinner stands where the figure will, so the tile
                           keeps its height and nothing shifts when it lands. -->
                      <Spinner
                        classes="justify-start h-4"
                        spinner-color="var(--c-dark-8)"
                        spinner-size="14px"
                      />
                      <p class="text-tiny text-surface-6 mt-1 normal-case">
                        of {{ assignedFormatted(getSubscriptionDefProp(subscription, metric), metric) }}
                      </p>
                      <div class="mt-2 h-1.5 rounded-full bg-surface-1 overflow-hidden">
                        <div class="h-full w-full rounded-full bg-main/40 animate-pulse"></div>
                      </div>
                    </div>
                    <div v-if="!subscription.usage && !countingMetrics(subscription).length && usageLoading" class="col-span-full">
                      <Spinner text="Loading usage..." classes="text-surface-7" spinner-color="var(--c-dark-8)" spinner-size="12px" />
                    </div>
                  </div>
                </div>
              </template>
            </li>
          </ul>
        </section>
        <section v-else class="text-center mt-8 p-8 rounded-xl bg-surface-1 max-w-md mx-auto">
          <p class="mb-1 font-medium text-surface-9 text-lg">No {{ activeTab }} subscriptions</p>
          <p v-if="activeTab === 'expired'" class="text-surface-6 text-sm">You don't have any expired subscription plans.</p>
          <template v-else>
            <p class="text-surface-6 text-sm mb-4">Pick a plan to start streaming with Castr.</p>
            <FormButton size="sm" to="/subscribe?category=live" label="Browse plans" />
          </template>
        </section>
        <ProjectUsagePanel />
        <section v-if="invoiceItems?.length > 0" class="mt-6">
          <h4 class="text-tiny uppercase tracking-wide text-surface-7 font-medium mb-3">Payment history</h4>
          <div class="bg-surface-1 rounded-xl overflow-hidden">
            <div class="hidden md:flex text-tiny text-surface-6 px-4 py-2.5">
              <span class="basis-3/12">Date</span>
              <span class="basis-6/12">Product</span>
              <span class="basis-2/12">Amount</span>
              <span class="basis-1/12 text-right">Invoice</span>
            </div>
            <div v-for="(invoice, index) in invoiceItems" :key="index"
              class="hidden md:flex text-sm text-surface-9 items-center px-4 py-2.5 hover:bg-surface-2 transition-colors">
              <span class="basis-3/12">{{ invoice.date }}</span>
              <span class="basis-6/12">{{ invoice.products }}</span>
              <span class="basis-2/12">{{ invoice.amount }}</span>
              <span class="basis-1/12 text-right">
                <FormButton size="sm" isIcon :href="invoice.invoice" type="secondary">
                  <icon-base>
                    <IconDownload />
                  </icon-base>
                </FormButton>
              </span>
            </div>
            <div class="divide-y divide-surface-3 md:hidden">
              <article v-for="(invoice, index) in invoiceItems" :key="`mobile-${index}`"
                class="grid grid-cols-[1fr_auto] gap-3 p-4 text-sm text-surface-9">
                <div class="min-w-0">
                  <p class="font-medium break-words">{{ invoice.products }}</p>
                  <p class="mt-1 text-surface-7">{{ invoice.date }}</p>
                  <p class="mt-2">{{ invoice.amount }}</p>
                </div>
                <FormButton class="self-center" size="sm" isIcon :href="invoice.invoice" type="secondary"
                  aria-label="Download invoice">
                  <icon-base>
                    <IconDownload />
                  </icon-base>
                </FormButton>
              </article>
            </div>
          </div>
        </section>
      </main>
    </div>
    <div class="lg:mt-24" v-else>
      <h3 class="text-lg text-center text-surface-9">Moderators don't have access to the Billing page.</h3>
      <p class="text-surface-6 text-center text-sm mb-2">Only the team owner and admins can access this page. Contact
        your
        team owner (<a class="underline" :href="'mailto:' + store.state.User.guestData.ownersEmail">{{
          store.state.User.guestData.ownersEmail }}</a>)
        for more information.</p>
      <p class="text-center">
        <FormButton to="/">
          Go to Livestreams
        </FormButton>
      </p>
    </div>
    <subscription-cancel-modal modal-id="modal-unsubscribe-package"
      :message="configurableSubscription ? 'Are you sure you want to unsubscribe from \'' + getSubscriptionName() + '\' package?' : ''"
      @modal-confirm="unsubscribeSubCategory">
    </subscription-cancel-modal>
    <alert-modal modal-id="modal-change-payment-info-failed" buttonAlignment="text-right"
      message="We only support to change credit card details for active account at the moment. If you are using Paypal or other payment type, please contact our support for help."
      ok-text="Got it" />
  </div>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped></style>
